import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { storage, formatDateTime } from '@/lib/storage';
import { Download, FileText, Megaphone, FileCheck, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const REPORTS = [
  { key: 'requests', label: 'Service Requests', icon: FileCheck, source: 'requests', headers: ['id', 'type', 'name', 'email', 'contact', 'purpose', 'address', 'status', 'date'] },
  { key: 'concerns', label: 'Concern Reports', icon: Megaphone, source: 'concerns', headers: ['id', 'category', 'urgency', 'location', 'name', 'contact', 'description', 'status', 'date'] },
  { key: 'messages', label: 'Contact Messages', icon: MessageSquare, source: 'messages', headers: ['id', 'name', 'email', 'topic', 'message', 'date'] },
  { key: 'programRegistrations', label: 'Program Registrations', icon: FileText, source: 'programRegistrations', headers: ['id', 'programTitle', 'name', 'contact', 'status', 'date'] },
];

const ExportReports = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const buildCsv = (headers, rows) => {
    return [headers, ...rows].map((row) => row.map((c) => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
  };

  const downloadFor = (report) => {
    let data = storage.get(report.source, []);
    if (from) data = data.filter((r) => new Date(r.date || r.submittedAt) >= new Date(from));
    if (to) data = data.filter((r) => new Date(r.date || r.submittedAt) <= new Date(`${to}T23:59:59`));

    if (data.length === 0) return toast.error('No records to export for the selected range.');

    const rows = data.map((r) => report.headers.map((h) => r[h] ?? ''));
    const csv = buildCsv(report.headers, rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${report.key}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} ${report.label.toLowerCase()}`);
  };

  const downloadJson = () => {
    const all = REPORTS.reduce((acc, r) => {
      acc[r.source] = storage.get(r.source, []);
      return acc;
    }, {});
    all.exportedAt = new Date().toISOString();
    const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `barangay-data-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Full backup exported (JSON)');
  };

  return (
    <div data-testid="admin-export-page">
      <AdminPageHeader
        title="Export Reports"
        description="Download CSV reports for offline analysis, audits, or filing with the Sangguniang Barangay."
        actions={
          <Button variant="outline" onClick={downloadJson} data-testid="export-json">
            <Download className="mr-2 h-4 w-4" /> Full backup (JSON)
          </Button>
        }
      />

      <Card className="p-5 mb-6 border-0 shadow-sm-soft">
        <h3 className="font-heading font-semibold mb-3">Optional date range</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[180px]" data-testid="export-from" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[180px]" data-testid="export-to" />
          </div>
          {(from || to) && <Button variant="ghost" onClick={() => { setFrom(''); setTo(''); }}>Clear range</Button>}
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {REPORTS.map((r) => {
          const data = storage.get(r.source, []);
          return (
            <Card key={r.key} className="p-6 border-0 card-elevated" data-testid={`export-${r.key}`}>
              <div className="flex items-center justify-between">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
                  <r.icon className="h-5 w-5" />
                </div>
                <Badge variant="secondary" className="bg-primary/10 text-primary">{data.length} records</Badge>
              </div>
              <h3 className="mt-4 font-heading font-semibold text-lg">{r.label}</h3>
              <p className="mt-1 text-sm text-muted-foreground">CSV will include: {r.headers.join(', ')}.</p>
              <Button onClick={() => downloadFor(r)} className="mt-5 w-full bg-gradient-primary text-primary-foreground" data-testid={`download-${r.key}`}>
                <Download className="mr-2 h-4 w-4" /> Download CSV
              </Button>
            </Card>
          );
        })}
      </div>

      <Card className="mt-6 p-6 border-dashed bg-secondary/40">
        <h4 className="font-heading font-semibold">Note for prototype</h4>
        <p className="mt-1 text-sm text-muted-foreground">
          All data is stored locally in the browser via <code className="px-1.5 py-0.5 rounded bg-muted text-xs">localStorage</code>. In production, these reports will pull from the barangay's central database with full audit trails.
        </p>
      </Card>
    </div>
  );
};

export default ExportReports;
